// client/src/components/bank/SubmitVatCreditBid.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { parseCurrencyInput, handleCurrencyInputChange, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { sanitizeInput } from '../utils/sanitize';
import { Edit } from 'lucide-react';
import VatCreditAttachmentList from '../vatCredit/VatCreditAttachmentList';
import {
  formatCreditTypeAndPeriod,
  formatMaturity,
  formatCoverage,
  formatRequestedRateTypes,
  toRomeDateInputValue,
  VAT_CREDIT_DAY_COUNT_BASES
} from '../vatCredit/vatCreditUtils';

const CURRENCY_FIELDS = ['desiredAmount', 'fees'];

const SubmitVatCreditBid = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [request, setRequest] = useState(null);
  const [formData, setFormData] = useState({
    desiredAmount: '', // Importo massimo
    rateType: '',
    baseRate: '',
    interestRate: '',
    dayCountBasis: '',
    officialMaturityDate: '',
    commission: '',
    fees: '',
    notes: ''
  });
  const [maxAmount, setMaxAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [bidSubmitted, setBidSubmitted] = useState(false);

  // Helper function to parse interest rate supporting both comma and dot
  const parseInterestRate = (value) => {
    if (value === null || value === undefined || value === '') return NaN;

    const normalizedValue = value.toString().trim().replace(',', '.');
    return parseFloat(normalizedValue);
  };

  // Tasso variabile: solo Euribor + spread
  const getBaseRateOptions = () => ([
    { value: 'euribor_1w', label: t('bids.euribor1Week') },
    { value: 'euribor_1m', label: t('bids.euribor1Month') },
    { value: 'euribor_3m', label: t('bids.euribor3Months') },
    { value: 'euribor_6m', label: t('bids.euribor6Months') },
    { value: 'euribor_12m', label: t('bids.euribor12Months') }
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requestRes = await axios.get(`/api/vat-credit-requests/${id}`);
        const fetchedRequest = requestRes.data.request;
        setRequest(fetchedRequest);
        setMaxAmount(fetchedRequest.amount);
        // Se l'azienda accetta un solo tipo di tasso, l'offerta è vincolata a quello
        if (fetchedRequest.requestedRateTypes?.length === 1) {
          setFormData(prev => ({ ...prev, rateType: fetchedRequest.requestedRateTypes[0] }));
        }
        setLoading(false);
      } catch (err) {
        setError(t('common.errors.fetchRequestDetails'));
        setLoading(false);
      }
    };

    fetchData();
  }, [id, t]);

  const onChange = e => {
    if (CURRENCY_FIELDS.includes(e.target.name)) {
      handleCurrencyInputChange(e, setFormData, formData, e.target.name);
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    // Clear error message when user starts typing again
    if (error) {
      setError('');
    }
  };

  const onSubmit = async e => {
    e.preventDefault();
    setError('');

    const desiredAmount = parseCurrencyInput(formData.desiredAmount);
    const interestRateValue = parseInterestRate(formData.interestRate);
    const commissionValue = parseInterestRate(formData.commission);
    const feesValue = parseCurrencyInput(formData.fees);

    // Restituisce il messaggio del primo errore, o null se il form è valido
    const getValidationError = () => {
      // --- Importo massimo ---
      if (isNaN(desiredAmount) || desiredAmount <= 0) return t('vatCredit.validation.validMaxAmount');
      if (desiredAmount > maxAmount) {
        return t('common.errors.amountExceedsMax', {
          maxAmount: formatCurrencyWithSymbol(maxAmount, request.currency || 'EUR')
        });
      }

      // --- Tasso ---
      if (formData.rateType !== 'fixed' && formData.rateType !== 'floating') return t('common.errors.selectRateType');
      if (formData.rateType === 'floating' && !formData.baseRate) return t('common.errors.selectBaseRate');
      if (isNaN(interestRateValue)) return t('common.errors.validInterestRate');
      if (!formData.dayCountBasis) return t('vatCredit.validation.dayCountBasisRequired');

      // --- Maturity, commissioni e spese ---
      if (!formData.officialMaturityDate) return t('vatCredit.validation.officialMaturityRequired');
      if (formData.officialMaturityDate <= toRomeDateInputValue(request.executionDate)) {
        return t('vatCredit.validation.officialMaturityAfterExecution');
      }
      if (isNaN(commissionValue) || commissionValue < 0) return t('vatCredit.validation.validCommission');
      if (formData.fees === '' || isNaN(feesValue) || feesValue < 0) return t('vatCredit.validation.validFees');
      return null;
    };

    const validationError = getValidationError();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);

    try {
      const bidData = {
        requestId: request.id || request._id,
        desiredAmount,
        rateType: formData.rateType,
        baseRate: formData.rateType === 'floating' ? formData.baseRate : null,
        interestRate: interestRateValue,
        dayCountBasis: formData.dayCountBasis,
        officialMaturityDate: formData.officialMaturityDate,
        commission: commissionValue,
        fees: feesValue,
        notes: sanitizeInput(formData.notes)
      };

      await axios.post('/api/vat-credit-bids', bidData);

      setBidSubmitted(true);

      setTimeout(() => {
        navigate('/bank/vat-credit/past-bids', {
          state: {
            bidSubmitted: true,
            message: t('requests.bidSuccessSubmitted'),
            activeTab: 'pending'
          }
        });
      }, 2500);

    } catch (err) {
      console.error('Error submitting VAT credit bid:', err);
      setError(err.response?.data?.message || t('common.errors.submitBid'));
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loadingRequestDetails')}</div>;
  }

  if (!request) {
    return <div className="error-message">{error || t('common.requestNotFound')}</div>;
  }

  // Tipi di tasso ammessi dall'azienda (le richieste legacy li ammettono tutti)
  const allowedRateTypes = request.requestedRateTypes?.length ? request.requestedRateTypes : ['fixed', 'floating'];
  const isRateTypeLocked = allowedRateTypes.length === 1;

  if (bidSubmitted) {
    return (
      <div className="card">
        <div className="alert-success" style={{ padding: '20px', margin: '20px 0' }}>
          <h3>{t('requests.bidSuccessSubmitted')}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="company-dashboard">

      {/* Navigation button outside and above the card */}
      <div className="page-navigation" style={{ marginBottom: '14px' }}>
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate('/bank/vat-credit/open-requests')}
        >
          ← {t('bids.backToNewBids')}
        </button>
      </div>

      <div className="card">
        <div className="card-title">
          <Edit size={20} />
          <span>{t('vatCredit.requestDetails')}</span>
          <div>
            <span className="status status-open">{t('status.open')}</span>
          </div>
        </div>

        <div className="request-info detail-list-lined">
          {/* Horizontal info box - same style as BidDetail */}
          <div className="allocation-details-card">
            <div style={{ display: 'flex', gap: '40px', justifyContent: 'center' }}>
              <div>
                <strong>{t('requests.requestId')}:</strong> #{request.requestId}
              </div>
              <div>
                <strong>{t('requests.company')}:</strong> {request.company?.name || t('bids.notAvailable')}
              </div>
              <div>
                <strong>{t('profile.vatNumber')}:</strong> {request.company?.vatNumber || t('profile.vatNumberNotSpecified')}
              </div>
              <div>
                <strong>{t('requests.datePosted')}:</strong> {new Date(request.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>

          <h3 style={{ marginTop: '16px' }}>{t('vatCredit.title')}</h3>

          <div className="detail-item">
            <div className="detail-label">{t('requests.currency')}:</div>
            <div className="detail-value">{request.currency || 'EUR'}</div>
          </div>

          {request.creditType && (
            <>
              <div className="detail-item">
                <div className="detail-label">{t('vatCredit.creditPeriod')}:</div>
                <div className="detail-value">{formatCreditTypeAndPeriod(request, t)}</div>
              </div>

              <div className="detail-item">
                <div className="detail-label">{t('vatCredit.refundRequestedAmount')}:</div>
                <div className="detail-value">{formatCurrencyWithSymbol(request.refundRequestedAmount, request.currency || 'EUR')}</div>
              </div>

              <div className="detail-item">
                <div className="detail-label">{t('vatCredit.coverage')}:</div>
                <div className="detail-value">{formatCoverage(request, t)}</div>
              </div>
            </>
          )}

          <div className="detail-item">
            <div className="detail-label">{t('vatCredit.maturityDate')}:</div>
            <div className="detail-value">{formatMaturity(request, t)}</div>
          </div>

          <div className="detail-item">
            <div className="detail-label">{t('vatCredit.amountToAssign')}:</div>
            <div className="detail-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</div>
          </div>

          {request.requestedRateTypes?.length > 0 && (
            <div className="detail-item">
              <div className="detail-label">{t('vatCredit.requestedRateType')}:</div>
              <div className="detail-value">{formatRequestedRateTypes(request, t)}</div>
            </div>
          )}

          <div className="detail-item">
            <div className="detail-label">{t('vatCredit.bidDeadline')}:</div>
            <div className="detail-value">
              {new Date(request.bidDeadline).toLocaleDateString()}
              {' '}
              <span style={{ color: '#6c757d' }}>
                {new Date(request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} CET
              </span>
            </div>
          </div>

          <div className="detail-item">
            <div className="detail-label">{t('vatCredit.executionDate')}:</div>
            <div className="detail-value">{new Date(request.executionDate).toLocaleDateString()}</div>
          </div>

          {/* Display company notes if available */}
          {request.notes && (
            <div className="detail-item">
              <div className="detail-label">{t('requests.companyNotes')}:</div>
              <div className="detail-value request-notes">{request.notes}</div>
            </div>
          )}
        </div>

        <div style={{ marginTop: '16px' }}>
          <VatCreditAttachmentList
            requestId={request.id || request._id}
            attachments={request.attachments}
          />
        </div>

        <div className="bid-form-container" style={{ marginTop: '36px' }}>
          <h3 style={{
            color: 'var(--primary)',
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '18px',
            textDecoration: 'underline'
          }}>
            {t('requests.submitYourBid')}
          </h3>

          {/* Display error message prominently above form */}
          {error && (
            <div className="alert alert-danger">{error}</div>
          )}

          <form id="vat-credit-bid-form" onSubmit={onSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="desiredAmount">{t('vatCredit.desiredAmount')} ({request.currency || 'EUR'}) *</label>
              <input
                type="text"
                id="desiredAmount"
                name="desiredAmount"
                placeholder={t('requests.enterBidAmount')}
                value={formData.desiredAmount}
                onChange={onChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="rateType">{t('vatCredit.bidRateType')} *</label>
              <select
                id="rateType"
                name="rateType"
                className="form-select-custom"
                value={formData.rateType}
                onChange={onChange}
                disabled={isRateTypeLocked}
                required
              >
                <option value="">{t('requests.selectRateType')}</option>
                {allowedRateTypes.includes('fixed') && <option value="fixed">{t('vatCredit.bidRateTypes.fixed')}</option>}
                {allowedRateTypes.includes('floating') && <option value="floating">{t('vatCredit.bidRateTypes.floating')}</option>}
              </select>
              {isRateTypeLocked && <small>{t('vatCredit.rateTypeLockedHelp')}</small>}
            </div>

            {/* Base Rate Selection - Show for floating rate */}
            {formData.rateType === 'floating' && (
              <div className="form-group">
                <label htmlFor="baseRate">{t('vatCredit.euriborTenor')} *</label>
                <select
                  id="baseRate"
                  name="baseRate"
                  className="form-select-custom"
                  value={formData.baseRate}
                  onChange={onChange}
                  required
                >
                  <option value="">{t('bids.selectBaseRate')}</option>
                  {getBaseRateOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="interestRate">
                {formData.rateType === 'floating'
                  ? t('bids.interestRateFloating')
                  : t('bids.interestRateFixed')}
              </label>
              <input
                type="number"
                id="interestRate"
                name="interestRate"
                placeholder={formData.rateType === 'floating'
                  ? t('requests.enterSpread')
                  : t('requests.enterInterestRate')}
                min="-2"
                step="0.01"
                value={formData.interestRate}
                onChange={onChange}
                onWheel={(e) => e.target.blur()}
                required
                disabled={!formData.rateType}
              />
            </div>

            <div className="form-group">
              <label htmlFor="dayCountBasis">{t('vatCredit.dayCountBasis')} *</label>
              <select
                id="dayCountBasis"
                name="dayCountBasis"
                className="form-select-custom"
                value={formData.dayCountBasis}
                onChange={onChange}
                required
              >
                <option value="">{t('vatCredit.selectDayCountBasis')}</option>
                {VAT_CREDIT_DAY_COUNT_BASES.map(basis => (
                  <option key={basis} value={basis}>{t(`vatCredit.dayCountBases.${basis}`)}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="officialMaturityDate">{t('vatCredit.officialMaturityDate')} *</label>
              <input
                type="date"
                id="officialMaturityDate"
                name="officialMaturityDate"
                value={formData.officialMaturityDate}
                onChange={onChange}
                min={toRomeDateInputValue(request.executionDate)}
                required
              />
              <small>{t('vatCredit.officialMaturityDateHelp')}</small>
            </div>

            <div className="form-group">
              <label htmlFor="commission">{t('vatCredit.commission')} (%) *</label>
              <input
                type="number"
                id="commission"
                name="commission"
                placeholder={t('vatCredit.enterCommission')}
                min="0"
                step="0.01"
                value={formData.commission}
                onChange={onChange}
                onWheel={(e) => e.target.blur()}
                required
              />
              <small>{t('vatCredit.commissionHelp')}</small>
            </div>

            <div className="form-group">
              <label htmlFor="fees">{t('vatCredit.fees')} (€) *</label>
              <input
                type="text"
                id="fees"
                name="fees"
                placeholder="0"
                value={formData.fees}
                onChange={onChange}
                required
              />
              <small>{t('vatCredit.feesHelp')}</small>
            </div>

            <div className="form-group">
              <label htmlFor="notes">{t('requests.additionalNotes')}</label>
              <textarea
                id="notes"
                name="notes"
                rows="4"
                placeholder={t('requests.additionalBidInfo')}
                value={formData.notes}
                onChange={onChange}
              ></textarea>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => navigate('/bank/vat-credit/open-requests')}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? t('actions.submitting') : t('bids.submitBid')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubmitVatCreditBid;
